import { Link, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Loading from '../../../common/Loading';
import { applicantService } from '../../../../services/applicant.service';
import CustomTable from '../../../common/table/CustomTable';
import { Column } from '../../../common/table/types';
import StatusChip from '../../../common/StatusChip';
import { STATUS_CONFIG } from '../../../../utils/common';
import { formatDate } from '../../../../utils/date';
import { DEFAULT_ROWS_PER_PAGE, JIRA_TICKET_URL } from '../../../../utils/constants';

interface DuplicateApplicantData {
  issueId: string;
  vacancy: string;
  currentStatus: string;
  createdAt: string;
}

interface DuplicateApplicantProps {
  applicantEmail?: string;
  jiraId?: string;
}

const DuplicateApplicants: React.FC<DuplicateApplicantProps> = ({ applicantEmail, jiraId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [duplicatesData, setDuplicatesData] = useState<DuplicateApplicantData[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!applicantEmail) {
      setIsLoading(false);
      return;
    }

    const fetchDuplicates = async () => {
      try {
        setIsLoading(true);
        
        const duplicatesResponse = await applicantService.search({
          email: applicantEmail,
          page,
          limit: rowsPerPage,
        });

        const rawData = duplicatesResponse.data.data || [];
        const filteredData = rawData.filter(item => item.issueId !== jiraId);

        setDuplicatesData(filteredData);
        setTotalCount(duplicatesResponse.data.count || 0);
      } catch (error) {
        setDuplicatesData([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDuplicates();
  }, [applicantEmail, page, rowsPerPage, jiraId]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setRowsPerPage(newLimit);
    setPage(1);
  };

  const columns: Column<DuplicateApplicantData>[] = [
    { 
      field: 'createdAt', 
      headerName: 'Applied Date', 
      width: "40px", 
      sortable: false, 
      align: 'center',
      renderCell: (row) => (
        <Box display="flex" justifyContent="center" width="100%">
          {formatDate(row.createdAt)}
        </Box>
      ),
    },
    { 
      field: 'vacancy', 
      headerName: 'Vacancy', 
      width: "150px",
      sortable: false 
    },
    { 
      field: 'currentStatus', 
      headerName: 'Last Status', 
      width: '40px',
      sortable: false,
      align: 'center',
      renderCell: (row) => row.currentStatus ? <StatusChip status={row.currentStatus} statusConfig={STATUS_CONFIG} /> : '',
    },
    { 
      field: 'issueId', 
      headerName: 'Jira ID', 
      width: "50px", 
      sortable: false,
      renderCell: (row) => row.issueId ?
        <Link
          href={`${JIRA_TICKET_URL}${row.issueId}`}
          target="_blank" rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {row.issueId}
        </Link> : '-'
    }
  ];

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box className="view-section">
        <CustomTable<DuplicateApplicantData>
          columns={columns}
          rows={duplicatesData}
          hasPagination={true}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={() => { }}
          sortField={undefined}
          sortDirection={'asc'}
        />
      </Box>
    </Box>
  );
};

export default DuplicateApplicants;
